import MUIGrid from '@/components/MUI-Components/MUI-Grid';
import MUITypography from '@/components/MUI-Components/MUI-Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import React, {memo} from 'react';
import type {SxProps, Theme} from '@mui/material/styles';
import {fontFamily} from '@/styles/tokens';

interface BlockchainBannerProps {
    BannerClass?: string;
    heading: React.ReactNode;
    subheading?: React.ReactNode;
    headingLevel?: React.ElementType;
    bannerSx?: SxProps<Theme>;
}

// Migrated from _header.scss `.new-blockchain-main-wrapper` (sole consumer — no other
// component/page references this class).
const newBlockchainMainWrapperSx = {color: '#fff'} as const;

// Migrated from _header.scss `.banner-header` (base + mobileScreen/max-430px override) and
// globals.scss `.new-blockchain-main-wrapper .banner-header` (tabScreen/max-768px override).
// The 430px and 768px blocks are both `!important` at equal specificity, so whichever compiled
// last wins — globals.scss's own body always compiles after _header.scss's `@import`, so the
// 768px rule (48px) has always won even inside the narrower <=430px range, and the 430px rule's
// 35px has never actually rendered at any viewport width. Preserved in this exact order (430px
// block before 768px block) to keep that existing (likely unintended) computed behavior
// unchanged — flagged here for a separate, deliberate decision, not fixed as part of this pass.
const bannerHeaderSx = {
    fontFamily: `${fontFamily.heading} !important`,
    fontSize: '70px',
    textAlign: 'left',
    letterSpacing: '1px !important',
    fontWeight: '700 !important',
    '@media (max-width: 430px)': {fontSize: '35px !important'},
    '@media (max-width: 768px)': {fontSize: '48px !important'}
} as const;

// Migrated from _header.scss `.banner-sub-header` (base + mobileScreen/max-430px override,
// sole owner — uncontested by any other file).
const bannerSubHeaderSx = {
    fontFamily: `${fontFamily.body} !important`,
    fontSize: '28px',
    lineHeight: '30px !important',
    fontWeight: '700 !important',
    '@media (max-width: 430px)': {fontSize: '18px !important'}
} as const;

const BlockchainBanner = (props: BlockchainBannerProps) => {
    const {BannerClass, heading, subheading, headingLevel = 'h1', bannerSx} = props;
    return (
        <MUIGrid item xs={12} className={BannerClass || ''} sx={bannerSx}>
            <Box className="new-blockchain-main-wrapper" sx={newBlockchainMainWrapperSx}>
                <Container maxWidth="xl">
                    <Box className="new-banner-content-wrapper" sx={{padding: 'calc(50vh - 280px) 0 !important'}}>
                        <Box>
                            <MUITypography component={headingLevel} className="banner-header" sx={bannerHeaderSx}>
                                {heading}
                            </MUITypography>
                            <MUITypography className="banner-sub-header" sx={bannerSubHeaderSx}>
                                {subheading}
                            </MUITypography>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </MUIGrid>
    );
};

export default memo(BlockchainBanner);
